import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  Code2,
  Shield,
  Sparkles,
  Wand2,
  Bug,
  Palette,
  Zap,
  Server,
  RefreshCw,
  AlertTriangle,
  ChevronRight,
  Layers
} from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { toast } from 'sonner';
import ChatInput from '../../../components/ai/ChatInput';
import ChatMessage from '../../../components/ai/ChatMessage';
import ConversationSidebar from '../../../components/ai/ConversationSidebar';
import TaskPanel from '../../../components/ai/TaskPanel';

/**
 * AI Command Center - FundElite Developer Agent
 * ChatGPT-style interface with strict admin controls
 * Phase 1: Simulated execution
 */
const AICommandCenter = () => {
  const { user, getAuthHeaders } = useAuth();
  const messagesEndRef = useRef(null);
  const [layout, setLayout] = useState('empty'); // 'empty' or 'active'
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentTask, setCurrentTask] = useState(null);
  const [filter, setFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(true);

  const isSuperAdmin = user?.role === 'platform_owner' || user?.role === 'platform_admin';

  // Smart suggestions for empty state
  const suggestions = [
    'Fix dark mode text contrast issues',
    'Debug mobile layout problems',
    'Improve component performance',
    'Refactor large file into modules',
    'Add missing loading states'
  ];

  // Mock conversations for Phase 1
  const mockConversations = [
    {
      id: 'conv_1',
      title: 'Fix CLIPlanner dark mode',
      preview: 'The dark mode has white panels that need to be fixed...',
      type: 'ui',
      status: 'patch_ready',
      createdAt: new Date(Date.now() - 3600000).toISOString(),
      updatedAt: new Date(Date.now() - 1800000).toISOString()
    },
    {
      id: 'conv_2',
      title: 'Mobile hamburger menu bug',
      preview: 'Menu does not close after navigation on mobile...',
      type: 'bug',
      status: 'approved',
      createdAt: new Date(Date.now() - 86400000).toISOString(),
      updatedAt: new Date(Date.now() - 43200000).toISOString()
    },
    {
      id: 'conv_3',
      title: 'Add pagination to API endpoint',
      preview: 'The dispute letters endpoint needs pagination...',
      type: 'backend',
      status: 'planning',
      createdAt: new Date(Date.now() - 172800000).toISOString(),
      updatedAt: new Date(Date.now() - 86400000).toISOString()
    }
  ];

  useEffect(() => {
    if (isSuperAdmin) {
      fetchConversations();
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    setLoadingConversations(true);
    try {
      const response = await fetch('/api/admin/ai/dev-agent/conversations', {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations?.length > 0 ? data.conversations : mockConversations);
      } else {
        setConversations(mockConversations);
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
      setConversations(mockConversations);
    } finally {
      setLoadingConversations(false);
    }
  };

  const classifyTask = (text) => {
    const lowerText = text.toLowerCase();
    if (lowerText.includes('dark mode') || lowerText.includes('color') || lowerText.includes('ui') || 
        lowerText.includes('layout') || lowerText.includes('css') || lowerText.includes('style')) {
      return 'ui';
    }
    if (lowerText.includes('bug') || lowerText.includes('fix') || lowerText.includes('broken') || 
        lowerText.includes('error') || lowerText.includes('not working')) {
      return 'bug';
    }
    if (lowerText.includes('api') || lowerText.includes('backend') || lowerText.includes('endpoint') || 
        lowerText.includes('database') || lowerText.includes('server')) {
      return 'backend';
    }
    if (lowerText.includes('add') || lowerText.includes('new') || lowerText.includes('feature') || 
        lowerText.includes('implement')) {
      return 'feature';
    }
    if (lowerText.includes('refactor') || lowerText.includes('optimize') || lowerText.includes('clean')) {
      return 'refactor';
    }
    return 'ui';
  };

  const assessRisk = (text, type) => {
    const lowerText = text.toLowerCase();
    if (lowerText.includes('auth') || lowerText.includes('billing') || lowerText.includes('payment') ||
        lowerText.includes('password') || lowerText.includes('secret') || lowerText.includes('.env')) {
      return { level: 'critical', reason: 'Involves authentication, billing, or secrets' };
    }
    if (type === 'backend' || lowerText.includes('database') || lowerText.includes('api')) {
      return { level: 'medium', reason: 'Backend changes require careful review' };
    }
    if (type === 'ui' || type === 'bug') {
      return { level: 'low', reason: 'UI-only changes with no backend impact' };
    }
    return { level: 'medium', reason: 'Standard code modification' };
  };

  const generateMockPlan = (text, type) => {
    const plans = {
      ui: [
        'Identify affected components and CSS files',
        'Audit current color values and contrast ratios',
        'Replace hardcoded colors with design tokens',
        'Apply dark mode classes consistently',
        'Test across all theme states'
      ],
      bug: [
        'Reproduce the issue in development environment',
        'Trace the bug to its root cause',
        'Implement targeted fix without side effects',
        'Add regression test to prevent recurrence',
        'Verify fix across affected scenarios'
      ],
      backend: [
        'Review current endpoint implementation',
        'Design changes with backward compatibility',
        'Update data models if needed',
        'Implement changes with proper error handling',
        'Add API tests and update documentation'
      ],
      feature: [
        'Define feature requirements and scope',
        'Design component/API structure',
        'Implement core functionality',
        'Add UI/UX polish and edge case handling',
        'Write tests and documentation'
      ],
      refactor: [
        'Analyze current code structure and dependencies',
        'Identify refactoring opportunities',
        'Break into smaller, testable changes',
        'Apply changes incrementally',
        'Verify no regressions introduced'
      ]
    };
    return plans[type] || plans.ui;
  };

  const generateMockFiles = (text, type) => {
    const filePatterns = {
      ui: [
        { path: '/app/frontend/src/pages/relevant/Component.jsx', lines: 250 },
        { path: '/app/frontend/src/pages/relevant/Component.css', lines: 180 }
      ],
      bug: [
        { path: '/app/frontend/src/components/AffectedComponent.jsx', lines: 320 }
      ],
      backend: [
        { path: '/app/backend/server.py', lines: 10500 },
        { path: '/app/backend/routes/relevant_routes.py', lines: 450 }
      ],
      feature: [
        { path: '/app/frontend/src/pages/NewFeature.jsx', lines: 0 },
        { path: '/app/backend/routes/new_routes.py', lines: 0 }
      ],
      refactor: [
        { path: '/app/frontend/src/pages/LargeFile.jsx', lines: 2600 }
      ]
    };
    return filePatterns[type] || filePatterns.ui;
  };

  const handleSendMessage = async ({ text, attachments }) => {
    if (!text.trim() && attachments.length === 0) return;

    // Switch to active layout
    setLayout('active');

    // Create user message
    const userMessage = {
      id: `msg_${Date.now()}`,
      role: 'user',
      content: text,
      attachments,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    // Simulate AI processing
    setTimeout(() => {
      const taskType = classifyTask(text);
      const risk = assessRisk(text, taskType);
      const files = generateMockFiles(text, taskType);
      const plan = generateMockPlan(text, taskType);

      // Create or update task
      const newTask = {
        id: selectedConversation || `task_${Date.now()}`,
        title: text.length > 50 ? text.substring(0, 50) + '...' : text,
        type: taskType,
        status: 'planning',
        risk: risk.level,
        riskReason: risk.reason,
        files,
        tokens: Math.floor(Math.random() * 3000) + 1500,
        cost: (Math.random() * 0.02 + 0.005),
        planStatus: {
          classification: 'done',
          inspection: 'pending',
          plan: 'pending',
          approval: 'pending'
        },
        audit: [
          { action: 'Task created', user: user?.name || 'Admin', timestamp: new Date().toISOString() },
          { action: `Classified as ${taskType}`, user: 'System', timestamp: new Date().toISOString() }
        ]
      };

      setCurrentTask(newTask);

      // Create AI response
      const aiMessage = {
        id: `msg_${Date.now() + 1}`,
        role: 'assistant',
        timestamp: new Date().toISOString(),
        content: `I've analyzed your request and classified this as a **${taskType.toUpperCase()}** task.`,
        structured: {
          summary: `I'll help you with: "${text}"`,
          files: files,
          plan: plan,
          risk: risk.level,
          riskReason: risk.reason,
          impact: taskType === 'ui' 
            ? 'UI changes only - no backend impact'
            : taskType === 'backend'
              ? 'Backend changes - requires API testing'
              : 'Standard code modification',
          estimate: {
            tokens: newTask.tokens,
            cost: newTask.cost
          },
          awaitingApproval: true,
          approvalType: 'files',
          approvalMessage: 'Do you want me to inspect these files to create a detailed plan?'
        }
      };

      setMessages(prev => [...prev, aiMessage]);
      setIsLoading(false);

      // Add to conversations if new
      if (!selectedConversation) {
        const newConv = {
          id: newTask.id,
          title: newTask.title,
          preview: text.substring(0, 100),
          type: taskType,
          status: 'planning',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        setConversations(prev => [newConv, ...prev]);
        setSelectedConversation(newTask.id);
      }
    }, 1500);
  };

  const handleAction = useCallback((action) => {
    if (!currentTask) return;

    const timestamp = new Date().toISOString();
    let systemMessage = null;
    let aiResponse = null;

    switch (action) {
      case 'approve_inspection':
        setCurrentTask(prev => ({
          ...prev,
          status: 'inspecting',
          planStatus: { ...prev.planStatus, inspection: 'done' },
          audit: [...prev.audit, { action: 'File inspection approved', user: user?.name || 'Admin', timestamp }]
        }));
        
        systemMessage = { role: 'system', type: 'success', content: 'File inspection approved', timestamp };
        
        // Simulate plan generation after inspection
        setTimeout(() => {
          setCurrentTask(prev => ({
            ...prev,
            status: 'plan_ready',
            planStatus: { ...prev.planStatus, plan: 'done' },
            audit: [...prev.audit, { action: 'Plan generated', user: 'System', timestamp: new Date().toISOString() }]
          }));

          const planMessage = {
            id: `msg_${Date.now()}`,
            role: 'assistant',
            timestamp: new Date().toISOString(),
            content: 'I\'ve inspected the files and generated an implementation plan.',
            structured: {
              summary: 'Based on my analysis, here\'s the implementation plan:',
              plan: generateMockPlan(currentTask.title, currentTask.type),
              risk: currentTask.risk,
              estimate: { tokens: currentTask.tokens + 500, cost: currentTask.cost + 0.005 },
              awaitingApproval: true,
              approvalType: 'plan',
              approvalMessage: 'Review the plan above. Approve to proceed with patch generation.'
            }
          };
          setMessages(prev => [...prev, planMessage]);
        }, 2000);
        break;

      case 'approve_plan':
        setCurrentTask(prev => ({
          ...prev,
          status: 'approved',
          planStatus: { ...prev.planStatus, approval: 'approved' },
          audit: [...prev.audit, { action: 'Plan approved', user: user?.name || 'Admin', timestamp }]
        }));
        systemMessage = { role: 'system', type: 'success', content: 'Plan approved - ready to generate patch', timestamp };
        toast.success('Plan approved');
        break;

      case 'generate_patch':
        if (currentTask.status !== 'approved') {
          toast.error('Plan must be approved first');
          return;
        }
        
        setCurrentTask(prev => ({
          ...prev,
          status: 'patch_ready',
          patchStatus: { generated: true, tests: 'pending', deployed: false },
          patch: {
            files: prev.files.map(f => ({
              path: f.path,
              changes: `@@ -10,5 +10,8 @@
 // Existing code
-const oldValue = "light";
+const newValue = "dark";
+// Additional fixes
 export default Component;`
            }))
          },
          audit: [...prev.audit, { action: 'Patch generated', user: 'System', timestamp }]
        }));

        aiResponse = {
          id: `msg_${Date.now()}`,
          role: 'assistant',
          timestamp,
          content: 'I\'ve generated the patch. Please review the changes:',
          structured: {
            summary: 'Patch generated successfully. Review the diff below:',
            diff: currentTask.files.map(f => ({
              path: f.path,
              changes: `@@ -10,5 +10,8 @@
 // Existing code
-const oldValue = "light";
+const newValue = "dark";
+// Additional dark mode fixes
 export default Component;`
            })),
            warning: 'Phase 1: This is a simulated patch. No real changes will be made.',
            awaitingApproval: true,
            approvalType: 'patch',
            approvalMessage: 'Review the diff and approve to deploy, or request revisions.'
          }
        };
        toast.success('Patch generated (Simulated)');
        break;

      case 'run_tests':
        setCurrentTask(prev => ({
          ...prev,
          patchStatus: { ...prev.patchStatus, tests: 'passed' },
          audit: [...prev.audit, { action: 'Tests executed - all passed', user: 'System', timestamp }]
        }));

        aiResponse = {
          id: `msg_${Date.now()}`,
          role: 'assistant',
          timestamp,
          content: 'Tests completed successfully.',
          structured: {
            summary: 'All tests passed!',
            quickActions: [
              { label: 'View Test Report', action: 'view_tests' },
              { label: 'Approve & Deploy', action: 'approve_deploy' }
            ]
          }
        };
        toast.success('Tests passed (Simulated)');
        break;

      case 'approve_deploy':
        setCurrentTask(prev => ({
          ...prev,
          status: 'executed',
          patchStatus: { ...prev.patchStatus, deployed: true },
          audit: [...prev.audit, { action: 'Patch deployed', user: user?.name || 'Admin', timestamp }]
        }));
        systemMessage = { role: 'system', type: 'success', content: 'Patch deployed successfully (Simulated)', timestamp };
        toast.success('Deployed (Simulated) - No real changes made');
        break;

      case 'request_revision':
        setCurrentTask(prev => ({
          ...prev,
          audit: [...prev.audit, { action: 'Revision requested', user: user?.name || 'Admin', timestamp }]
        }));
        systemMessage = { role: 'system', type: 'info', content: 'Revision requested - describe what needs to change', timestamp };
        break;

      case 'rollback':
        setCurrentTask(prev => ({
          ...prev,
          status: 'rolled_back',
          patchStatus: { ...prev.patchStatus, deployed: false },
          audit: [...prev.audit, { action: 'Rollback executed', user: user?.name || 'Admin', timestamp }]
        }));
        systemMessage = { role: 'system', type: 'info', content: 'Rollback executed (Simulated)', timestamp };
        toast.info('Rolled back (Simulated)');
        break;

      case 'reject':
        setCurrentTask(prev => ({
          ...prev,
          status: 'rejected',
          planStatus: { ...prev.planStatus, approval: 'rejected' },
          audit: [...prev.audit, { action: 'Task rejected', user: user?.name || 'Admin', timestamp }]
        }));
        systemMessage = { role: 'system', type: 'error', content: 'Task rejected', timestamp };
        toast.info('Task rejected');
        break;

      case 'inspect_files':
        aiResponse = {
          id: `msg_${Date.now()}`,
          role: 'assistant',
          timestamp,
          content: 'Here are the files I\'ll need to inspect:',
          structured: {
            files: currentTask.files,
            awaitingApproval: true,
            approvalType: 'files',
            approvalMessage: 'Approve file inspection to proceed with plan generation.'
          }
        };
        break;

      case 'generate_plan':
        aiResponse = {
          id: `msg_${Date.now()}`,
          role: 'assistant',
          timestamp,
          content: 'Generating implementation plan...',
          structured: {
            plan: generateMockPlan(currentTask.title, currentTask.type),
            estimate: { tokens: currentTask.tokens, cost: currentTask.cost },
            awaitingApproval: true,
            approvalType: 'plan',
            approvalMessage: 'Review and approve this plan to proceed.'
          }
        };
        break;

      case 'show_diff':
        if (currentTask.patch) {
          aiResponse = {
            id: `msg_${Date.now()}`,
            role: 'assistant',
            timestamp,
            content: 'Here\'s the current diff:',
            structured: {
              diff: currentTask.patch.files
            }
          };
        }
        break;

      default:
        console.log('Unknown action:', action);
    }

    // Add messages to chat
    if (systemMessage) {
      setMessages(prev => [...prev, { id: `sys_${Date.now()}`, ...systemMessage }]);
    }
    if (aiResponse) {
      setMessages(prev => [...prev, aiResponse]);
    }

    // Update conversation status
    if (currentTask) {
      setConversations(prev => prev.map(c => 
        c.id === selectedConversation ? { ...c, status: currentTask.status, updatedAt: timestamp } : c
      ));
    }
  }, [currentTask, user?.name, selectedConversation]);

  const handleNewChat = () => {
    setSelectedConversation(null);
    setMessages([]);
    setCurrentTask(null);
    setLayout('empty');
  };

  const handleSelectConversation = (id) => {
    setSelectedConversation(id);
    setLayout('active');
    // In real implementation, load messages for this conversation
    // For now, just show placeholder
    setMessages([
      {
        id: 'placeholder',
        role: 'system',
        type: 'info',
        content: 'Conversation loaded',
        timestamp: new Date().toISOString()
      }
    ]);
    // Load mock task data
    const conv = conversations.find(c => c.id === id);
    if (conv) {
      setCurrentTask({
        id: conv.id,
        title: conv.title,
        type: conv.type,
        status: conv.status,
        risk: 'low',
        files: generateMockFiles(conv.title, conv.type),
        tokens: 2500,
        cost: 0.0075,
        planStatus: {
          classification: 'done',
          inspection: conv.status !== 'planning' ? 'done' : 'pending',
          plan: ['plan_ready', 'approved', 'patch_ready', 'executed'].includes(conv.status) ? 'done' : 'pending',
          approval: conv.status === 'approved' || conv.status === 'executed' ? 'approved' : 'pending'
        },
        patchStatus: ['patch_ready', 'executed'].includes(conv.status) ? {
          generated: true,
          tests: 'passed',
          deployed: conv.status === 'executed'
        } : null,
        audit: [
          { action: 'Task created', user: 'Admin', timestamp: conv.createdAt }
        ]
      });
    }
  };

  const handleSuggestionClick = (suggestion) => {
    handleSendMessage({ text: suggestion, attachments: [] });
  };

  // Access denied for non-super admins
  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0A1F14] via-[#0D2818] to-[#0A1F14]">
        <div className="max-w-md text-center p-8 rounded-2xl bg-[rgba(10,38,28,0.8)] border border-[rgba(142,218,182,0.15)]">
          <Shield className="w-16 h-16 mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-slate-400">
            AI Command Center requires Super Admin role (platform_owner or platform_admin).
          </p>
        </div>
      </div>
    );
  }

  // Empty state - centered layout
  if (layout === 'empty') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-[#0A1F14] via-[#0D2818] to-[#0A1F14] p-6">
        {/* Ambient glow */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 w-full max-w-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
              <Wand2 className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              What would you like to fix or improve?
            </h1>
            <p className="text-emerald-200/60">
              Describe an issue, improvement, or task for the platform
            </p>
            <Badge className="mt-3 bg-amber-500/20 text-amber-300 border border-amber-500/30">
              Phase 1: Simulated Mode
            </Badge>
          </div>

          {/* Chat Input */}
          <ChatInput
            onSend={handleSendMessage}
            placeholder="Describe an issue, improvement, or task..."
            showSuggestions={true}
            suggestions={suggestions}
            onSuggestionClick={handleSuggestionClick}
            isLoading={isLoading}
          />

          {/* Security Notice */}
          <div className="mt-8 flex items-center justify-center gap-2 text-xs text-slate-500">
            <Shield className="w-4 h-4" />
            <span>Super Admin Only • All actions logged • No auto-deploy</span>
          </div>
        </div>
      </div>
    );
  }

  // Active conversation - 3-column layout
  return (
    <div className="h-screen flex bg-gradient-to-br from-[#0A1F14] via-[#0D2818] to-[#0A1F14]">
      {/* Left Sidebar */}
      <div className="w-72 flex-shrink-0 hidden lg:block">
        <ConversationSidebar
          conversations={conversations}
          selectedId={selectedConversation}
          onSelect={handleSelectConversation}
          onNewChat={handleNewChat}
          filter={filter}
          onFilterChange={setFilter}
          loading={loadingConversations}
        />
      </div>

      {/* Center - Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[rgba(142,218,182,0.1)]">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
              <Code2 className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-medium text-white">
                {currentTask?.title || 'AI Command Center'}
              </h2>
              <p className="text-xs text-slate-500">Developer Agent</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-amber-500/20 text-amber-300 border border-amber-500/30 text-xs">
              Simulated
            </Badge>
            {currentTask && (
              <Badge className={`border text-xs ${
                currentTask.type === 'ui' ? 'bg-purple-500/20 text-purple-300 border-purple-500/30' :
                currentTask.type === 'bug' ? 'bg-red-500/20 text-red-300 border-red-500/30' :
                currentTask.type === 'backend' ? 'bg-amber-500/20 text-amber-300 border-amber-500/30' :
                'bg-blue-500/20 text-blue-300 border-blue-500/30'
              }`}>
                {currentTask.type}
              </Badge>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6">
          {messages.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <Sparkles className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                <p className="text-slate-500">Start a conversation</p>
              </div>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <ChatMessage
                  key={message.id}
                  message={message}
                  onAction={handleAction}
                  disabled={isLoading}
                  taskStatus={currentTask?.status}
                />
              ))}
              {isLoading && (
                <ChatMessage
                  message={{ role: 'assistant', isLoading: true }}
                />
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input */}
        <div className="p-4 border-t border-[rgba(142,218,182,0.1)]">
          <ChatInput
            onSend={handleSendMessage}
            placeholder="Continue the conversation..."
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Right Panel */}
      <div className="w-80 flex-shrink-0 hidden xl:block">
        <TaskPanel
          task={currentTask}
          onAction={handleAction}
          disabled={isLoading}
        />
      </div>
    </div>
  );
};

export default AICommandCenter;
